/* eslint-disable complexity */
import { format_detail_value as format_detail_value_helper } from './manage_verification_helpers';
import { get_auto_check_group_key } from './manage_verification_helpers';

function get_status_badge_class(tone) {
  if (tone === 'danger') return 'verification-status-pill verification-status-pill--danger';
  if (tone === 'success') return 'verification-status-pill verification-status-pill--success';
  if (tone === 'warning') return 'verification-status-pill verification-status-pill--warning';
  return 'verification-status-pill verification-status-pill--secondary';
}

function title_char(char) {
  return char.toUpperCase();
}

function normalize_run_status(value = '') {
  return String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
}

function is_completed_auto_run_status(value = '') {
  const normalized = normalize_run_status(value);
  if (!normalized) return false;

  return !['pending', 'not run', 'queued', 'in progress', 'processing', 'running'].includes(normalized);
}

function normalize_service_name(value = '') {
  return String(value || '').trim().toLowerCase();
}

export const manageVerificationSupportMethods = {
  has_auto_check_run(item = {}) {
    if (item?.verification_mode === 'manual') {
      return false;
    }

    const current_status = item?.raw_status?.status || item?.status;
    if (is_completed_auto_run_status(current_status)) {
      return true;
    }

    if (item?.raw_log?.status && is_completed_auto_run_status(item.raw_log.status)) {
      return true;
    }

    const history_items = Array.isArray(item?.history) ? item.history : [];
    return history_items.some((entry) => is_completed_auto_run_status(entry?.status));
  },
  auto_check_action_label(item = {}) {
    return this.has_auto_check_run(item) ? 'Re-run verification' : 'Run verification';
  },
  expand_matching_auto_check(check_item = {}) {
    const target_group_key = get_auto_check_group_key(check_item);
    const matched_item = this.selected_case_checks.find((item) => get_auto_check_group_key(item) === target_group_key);
    const target_key = String(matched_item?.id || check_item?.id || '');

    if (!target_key || this.expanded_check_keys.includes(target_key)) {
      return;
    }

    this.expanded_check_keys = [...this.expanded_check_keys, target_key];
  },
  open_document_preview(document = {}) {
    const preview_url = String(document?.file_url || '').trim();
    if (!preview_url) {
      this.set_run_feedback('No preview is available for this document.', 'warning');
      return;
    }

    this.document_preview_modal = {
      visible: true,
      title: document?.name || 'Document preview',
      url: preview_url,
      type: this.preview_document_type(document),
      detail: document
    };
  },
  close_document_preview() {
    this.document_preview_modal = {
      visible: false,
      title: '',
      url: '',
      type: '',
      detail: null
    };
  },
  preview_modal_src() {
    if (!this.document_preview_modal.url) return '';
    if (this.document_preview_modal.type === 'pdf') {
      return `${this.document_preview_modal.url}#toolbar=1&navpanes=0&view=FitH`;
    }

    return this.document_preview_modal.url;
  },
  download_document(document = {}) {
    const file_url = String(document?.file_url || '').trim();
    if (!file_url) {
      this.set_run_feedback('No file is available to download for this document.', 'warning');
      return;
    }

    if (typeof window !== 'undefined') {
      window.open(file_url, '_blank', 'noopener');
    }
  },
  async run_auto_check(check_item, event) {
    event?.stopPropagation?.();
    if (this.is_entity_verification_read_only) {
      this.set_run_feedback('Verification is view-only for entity users.', 'warning');
      return;
    }

    if (!this.can_run_auto_check(check_item)) {
      this.set_run_feedback('This verification is not linked to a runnable application form yet.', 'warning');
      return;
    }

    this.set_run_feedback('');
    this.set_auto_check_running(check_item, true);

    try {
      const result = await this.run_auto_verification_with_fallback(check_item);
      const success_message = result?.used_fallback
        ? `${check_item?.check || 'Verification'} ran successfully after matching the correct application form.`
        : `${check_item?.check || 'Verification'} ran successfully.`;
      this.set_run_feedback(success_message, 'success');
      await this.load_checks();
      this.$nextTick(() => {
        this.expand_matching_auto_check(check_item);
      });
    } catch (error) {
      this.set_run_feedback(
        error?.response?.data?.detail
          || error?.response?.data?.message
          || error?.message
          || `Unable to run ${check_item?.check || 'verification'}.`,
        'danger'
      );
    } finally {
      this.set_auto_check_running(check_item, false);
    }
  },
  open_sign_modal(agreement_title = 'Supplier agreement', signatory_name = '') {
    this.sign_modal = {
      visible: true,
      agreement_title,
      signatory_name: signatory_name || this.selected_case?.name || 'Onboarding signatory',
      document_id: this.selected_case?.application_id || 'DOC-VERIFICATION-2026'
    };
  },
  close_sign_modal() {
    this.sign_modal.visible = false;
  },
  complete_signature() {
    this.close_sign_modal();
  },
  format_field_label(value = '') {
    return String(value)
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, title_char);
  },
  format_detail_value(value) {
    return format_detail_value_helper(value);
  },
  format_match_status(value = '') {
    const normalized = this.normalize_match_status(value);
    if (!normalized) return 'Unable to Verify';

    return normalized
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, title_char);
  },
  normalize_match_status(value = '') {
    const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (normalized === 'yes') return 'match';
    if (normalized === 'no') return 'mismatch';
    return normalized;
  },
  status_badge_class(tone) {
    return get_status_badge_class(tone);
  },
  field_result_row_class(match) {
    if (match === true) return 'border-success-subtle bg-success-subtle';
    if (match === false) return 'border-danger-subtle bg-danger-subtle';
    return 'border-warning-subtle bg-warning-subtle';
  },
  verification_service_icon(item = {}) {
    const service_name = normalize_service_name(
      item?.check
      || item?.service?.name
      || item?.service?.service_name
      || item?.source
    );

    if (service_name.includes('kyc')) return 'bi-person-vcard';
    if (service_name.includes('identity') || service_name.includes('id verification') || service_name.includes('idv')) return 'bi-person-badge';
    if (service_name.includes('aml')) return 'bi-shield-check';
    if (service_name.includes('credit')) return 'bi-file-earmark-bar-graph';
    if (service_name.includes('cipc')) return 'bi-buildings';

    return 'bi-shield-check';
  },
  evidence_icon_class(tone) {
    return get_status_badge_class(tone);
  }
};
